import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import {
  CurrencyDollar,
  TrendUp,
  Buildings,
  Users,
  Tag,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const RevenuePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("brand");

  useEffect(() => {
    fetchRevenue();
  }, []);

  const fetchRevenue = async () => {
    try {
      const res = await api.get("/revenue/summary");
      setData(res.data);
    } catch {
      toast.error("Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin" />
      </div>
    );
  }

  const tabs = [
    { key: "brand", label: "By Brand", icon: Tag },
    { key: "hotel", label: "By Hotel", icon: Buildings },
    { key: "client", label: "By Client", icon: Users },
  ];

  const currentData = activeTab === "brand" ? data?.by_brand : activeTab === "hotel" ? data?.by_hotel : data?.by_client;

  return (
    <div className="p-4 md:p-8 max-w-7xl" data-testid="revenue-page">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Revenue</p>
        <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-navy">Revenue Reporting</h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">Based on Confirmed & Completed reservations only</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="bg-white border border-border p-3 md:p-5">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <span className="text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Revenue</span>
            <div className="w-7 h-7 md:w-8 md:h-8 bg-gold/10 flex items-center justify-center">
              <CurrencyDollar size={14} className="text-gold" />
            </div>
          </div>
          <p className="font-serif text-lg md:text-2xl text-navy" data-testid="revenue-total-sale">{formatCurrency(data?.overall?.total_sale)}</p>
        </div>
        <div className="bg-white border border-border p-3 md:p-5">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <span className="text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Cost</span>
            <div className="w-7 h-7 md:w-8 md:h-8 bg-red-50 flex items-center justify-center">
              <CurrencyDollar size={14} className="text-red-500" />
            </div>
          </div>
          <p className="font-serif text-lg md:text-2xl text-navy" data-testid="revenue-total-cost">{formatCurrency(data?.overall?.total_cost)}</p>
        </div>
        <div className="bg-white border border-border p-3 md:p-5">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <span className="text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Margin</span>
            <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-50 flex items-center justify-center">
              <TrendUp size={14} className="text-emerald-600" />
            </div>
          </div>
          <p className="font-serif text-lg md:text-2xl text-emerald-600" data-testid="revenue-total-margin">{formatCurrency(data?.overall?.total_margin)}</p>
        </div>
        <div className="bg-white border border-border p-3 md:p-5">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <span className="text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reservations</span>
            <div className="w-7 h-7 md:w-8 md:h-8 bg-navy/5 flex items-center justify-center">
              <ArrowsClockwise size={14} className="text-navy" />
            </div>
          </div>
          <p className="font-serif text-lg md:text-2xl text-navy" data-testid="revenue-count">{data?.overall?.reservation_count || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-border">
        <div className="flex border-b border-border overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 md:px-5 py-2.5 md:py-3 text-xs md:text-sm whitespace-nowrap transition-colors border-b-2 ${
                activeTab === tab.key
                  ? "border-b-gold text-navy font-medium"
                  : "border-b-transparent text-muted-foreground hover:text-navy"
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Table */}
        {currentData?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-border bg-cream/50">
                  <th className="text-left px-3 md:px-5 py-2 md:py-3 text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {activeTab === "brand" ? "Brand" : activeTab === "hotel" ? "Hotel" : "Client"}
                  </th>
                  {activeTab === "hotel" && (
                    <>
                      <th className="text-left px-3 md:px-5 py-2 md:py-3 text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Brand</th>
                      <th className="text-left px-3 md:px-5 py-2 md:py-3 text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">City</th>
                    </>
                  )}
                  <th className="text-right px-3 md:px-5 py-2 md:py-3 text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">#</th>
                  <th className="text-right px-3 md:px-5 py-2 md:py-3 text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Revenue</th>
                  <th className="text-right px-3 md:px-5 py-2 md:py-3 text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Cost</th>
                  <th className="text-right px-3 md:px-5 py-2 md:py-3 text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Margin</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((row, idx) => {
                  const name = activeTab === "brand" ? row.brand : activeTab === "hotel" ? row.hotel : row.client;
                  return (
                    <tr key={idx} className="border-b border-border last:border-0 hover:bg-cream/30 transition-colors">
                      <td className="px-3 md:px-5 py-2 md:py-3 font-medium text-navy text-xs md:text-sm">{name}</td>
                      {activeTab === "hotel" && (
                        <>
                          <td className="px-3 md:px-5 py-2 md:py-3 text-muted-foreground text-xs hidden sm:table-cell">{row.brand}</td>
                          <td className="px-3 md:px-5 py-2 md:py-3 text-muted-foreground text-xs hidden sm:table-cell">{row.city}</td>
                        </>
                      )}
                      <td className="px-3 md:px-5 py-2 md:py-3 text-right text-muted-foreground">{row.bookings}</td>
                      <td className="px-3 md:px-5 py-2 md:py-3 text-right font-medium text-navy">{formatCurrency(row.total_sale)}</td>
                      <td className="px-3 md:px-5 py-2 md:py-3 text-right text-muted-foreground hidden sm:table-cell">{formatCurrency(row.total_cost)}</td>
                      <td className="px-3 md:px-5 py-2 md:py-3 text-right font-medium text-emerald-600">{formatCurrency(row.margin)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <ArrowsClockwise size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No revenue data yet</p>
            <p className="text-sm text-muted-foreground mt-1">Revenue appears when reservations are marked Confirmed or Completed</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenuePage;
